import { CurrentCommunityInfo } from "client/Api/Communities/communityFeed";
import {
	Button,
	DropOverActionList,
	ExpandableText,
	IconButton
} from "client/Components";
import {
	Fragment,
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./CommunityInfo.module.less";
import { FeedPageAction, FeedPageActionContext } from "./CommunityFeedPage";
import {
	generateTrackingId,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import {
	ActionKind,
	reduceToAvailableActions,
	resolveActionTitle
} from "client_legacy/ndaw/actions";
import { actionComponentFactory } from "client/Components/ActionList/actionsRenderHelpers";
import { CommunityCoverImage } from "./CommunityCoverImage";
import { MemberStatistic } from "./MemberStatistic";
import { VisibilityLabel } from "./VisibilityLabel";
import { useCommunityMembersDialog } from "./CommunityMembersDialog";

export type CommunityInfoProps = {
	model: CurrentCommunityInfo;
	membershipActions: readonly FeedPageAction[];
	moreActions: readonly FeedPageAction[];
	actionContext: FeedPageActionContext;
	isMobile: boolean;
};

export const CommunityInfo = (props: CommunityInfoProps): ReactElement => {
	const {
		model,
		membershipActions,
		moreActions,
		actionContext,
		isMobile
	} = props;

	const {
		id,
		name,
		membersCount,
		description,
		visibility,
		image,
		userInfo
	} = model;

	const availableMembershipActions = useMemo(
		(): readonly FeedPageAction[] => reduceToAvailableActions(membershipActions, actionContext),
		[actionContext, membershipActions]
	);

	const [membersDialog, openMembersDialog] = useCommunityMembersDialog();

	const showMembersHandler = useCallback(
		() => {
			openMembersDialog({
				communityId: id,
				membersCount
			});
		},
		[
			id,
			membersCount,
			openMembersDialog
		]
	);

	const renderActions = (): ReactElement | null => {
		if (isMobile) {
			return null;
		}

		return (
			<>
				{availableMembershipActions.map(
					x => (
						<Button
							key={x.name}
							kind="secondary"
							text={x.title
								? resolveActionTitle(x.title, actionContext)
								: ""
							}
							icon={x.icon}
							onClick={() => x.execute?.(actionContext)}
							size="large"
							isDisabled={x.kind === ActionKind.Regular
								&& x.isDisabled?.(actionContext)
							}
							trackingId={x.name}
						/>
					)
				)}
				<MoreOptions
					actions={moreActions}
					actionContext={actionContext}
					menuTitle={name}
					trackingId="community-actions"
				/>
			</>
		);
	};

	return (
		<>
			<div
				{...setTrackingId("community-info")}
				className={styles.root}
			>
				{image && (
					<div className={styles.cover}>
						<CommunityCoverImage image={image} />
					</div>
				)}

				<div className={styles.infoSection}>
					<div className={styles.header}>
						<h2 className={styles.title}>
							{name}
						</h2>
						{renderActions()}
					</div>

					<div className={styles.statistics}>
						<VisibilityLabel visibility={visibility} />
						<span className={styles.statisticsSeparator}>|</span>
						<MemberStatistic
							membersCount={membersCount}
							onClick={
								userInfo.canAccess
									? showMembersHandler
									: undefined
							}
						/>
					</div>

					{isNotNilOrWhiteSpace(description) && (
						<div className={styles.description}>
							{isMobile
								? (
									<ExpandableText
										text={description}
										maxLines={3}
										trackingId="description"
									/>
								)
								: description
							}
						</div>
					)}
				</div>
			</div>

			{membersDialog}
		</>
	);
};
CommunityInfo.displayName = "CommunityInfo";

type MoreOptionsProps = {
	trackingId: string;
	menuTitle: string;
	actions: readonly FeedPageAction[];
	actionContext: FeedPageActionContext;
};

const MoreOptions = (props: MoreOptionsProps): ReactElement | null => {
	const {
		trackingId,
		menuTitle,
		actionContext,
		actions
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const availableActions = useMemo(
		(): readonly FeedPageAction[] => reduceToAvailableActions(actions, actionContext),
		[actionContext, actions]
	);

	if (availableActions.length === 0) {
		return null;
	}

	return (
		<>
			<IconButton
				kind="secondary"
				icon="ellipsis"
				onClick={() => setIsOpen((x) => !x)}
				ref={ref}
				trackingId={trackingId}
				shape="round"
				size="large"
				isActive={isOpen}
				bubbleClick={false}
			/>

			{isOpen && ref.current && (
				<DropOverActionList
					target={ref.current}
					targetAlignment="br"
					contentAlignment="tr"
					onClose={() => setIsOpen(false)}
					trackingId={generateTrackingId(trackingId, "__dropover")}
					menuTitle={menuTitle}
					bubbleClick={false}
				>
					{availableActions.map((x) => (
						<Fragment key={x.name}>
							{actionComponentFactory(x, actionContext)}
						</Fragment>
					))}
				</DropOverActionList>
			)}
		</>
	);
};
MoreOptions.displayName = "MoreOptions";